import { NgTemplateOutlet } from '@angular/common';
import { Component, effect, Input, OnChanges, SimpleChanges } from '@angular/core';
import { Filter } from '../../models/filter-models';
import { EndgameContent, JewelleryType } from '../../models/enums';
import { ItemDataService } from '../../services/item-data.service';
import { FlatItemData, JewelleryTierType, TieredJewellery } from '../../models/item-data-models';
import { ItemLabelComponent } from '../../common/item-label/item-label.component';
import { FormsModule } from '@angular/forms';
import { FilterService } from '../../services/filter.service';
import { JewelleryTierService } from '../../services/tiering/jewellery-tier.service';
import { JewelleryTierIconComponent } from '../../common/icons/jewellery-tier-icon/jewellery-tier-icon.component';
import { SelectComponent, SelectOptionComponent } from '../../common/select/select.component';
import { BaseComponent } from '../../common/base.component';
import { JewelleryTierNavigationTarget, NavigationService } from '../../services/navigation.service';
import { poe1 } from '../../../game';

export type JewelleryTierVm = {
  tier: JewelleryTierType;
  tierStyle: string;
  tierSubtitle?: string;
  isDragOver?: boolean;
  dragCounter?: number;
  items?: JewelleryTierItemVm[];
  itemTypes?: { itemType: string; items: JewelleryTierItemVm[] }[];
};

export type JewelleryTierItemVm = {
  name: string;
  image?: string;
  originalTier?: JewelleryTierType;
  isRareOnly?: boolean;
  restricted?: string;
  restrictedContent?: EndgameContent;
};

const JEWELLERY_CLASSES = ['Amulets', 'Rings', 'Belts'] as const;
const TIER_ORDER: JewelleryTierType[] = ['Excellent', 'Good', 'Special', 'Other'];

const TIER_SUBTITLES: Record<JewelleryTierType, string> = {
  Excellent: 'Very Valuable Bases',
  Good: 'Valuable Bases',
  Special: 'Remaining Restricted Bases',
  Other: 'Remaining Bases',
};

@Component({
  selector: 'app-jewellery-tiers',
  imports: [NgTemplateOutlet, FormsModule, ItemLabelComponent, JewelleryTierIconComponent, SelectComponent, SelectOptionComponent],
  templateUrl: './jewellery-tiers.component.html',
  styleUrls: ['./jewellery-tiers.component.scss', '../tier-tab-common.scss']
})
export class JewelleryTiersComponent extends BaseComponent implements OnChanges {
  @Input({ required: true }) filter!: Filter;
  @Input() changedOnly = false;

  itemData!: FlatItemData[];
  jewelleryTierData!: TieredJewellery[];

  jewelleryClassList!: { type: string, image?: string }[];
  jewelleryClass = JewelleryType.All;

  selectedJewelleryTierDataVm?: JewelleryTierVm[];
  tierStyleMap!: Record<JewelleryTierType, string>;
  draggedItem: JewelleryTierItemVm | null = null;
  private rareOnlyJewellery!: Set<string>;

  JewelleryType = JewelleryType;

  constructor(
    private itemDataService: ItemDataService,
    private filterService: FilterService,
    private jewelleryTierService: JewelleryTierService,
    private navigationService: NavigationService,
  ) {
    super();
    this.itemData = this.itemDataService.getFlatItemData();
    this.rareOnlyJewellery = new Set(this.itemDataService.getRareOnlyJewellery());

    effect(() => {
      const target = this.navigationService.jewelleryTierNavigationTarget();
      if (!target) return;
      this.navigationService.jewelleryTierNavigationTarget.set(null);
      this.applyNavigationTarget(target);
      setTimeout(() => this.navigationService.highlightBaseType(target.baseType), 0);
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['filter'] || changes['changedOnly']) {
      this.jewelleryTierData = this.jewelleryTierService.getJewelleryTierData();
      this.tierStyleMap = TIER_ORDER.reduce((acc, tier) => {
        acc[tier] = this.getTierStyle(tier);
        return acc;
      }, {} as Record<JewelleryTierType, string>);

      this.jewelleryClassList = [
        { type: JewelleryType.All, image: undefined },
        ...JEWELLERY_CLASSES.map(type => ({
          type,
          image: this.itemData.find(data => data.itemType === type)?.baseType.image,
        })),
      ];

      if (!this.jewelleryClass) {
        this.jewelleryClass = JewelleryType.All;
      }

      this.rebuildJewelleryTierDataVm();
    }
  }

  buildJewelleryTierDataVm(): JewelleryTierVm[] {
    return TIER_ORDER.map((tier) => {
      const vm: JewelleryTierVm = {
        tier,
        tierStyle: this.tierStyleMap[tier],
        tierSubtitle: TIER_SUBTITLES[tier],
      };

      if (this.jewelleryClass === JewelleryType.All) {
        vm.itemTypes = JEWELLERY_CLASSES.map(itemType => ({
          itemType,
          items: this.buildJewelleryTierItemVm(tier, itemType),
        })).filter(group => group.items.length > 0);
      } else {
        vm.items = this.buildJewelleryTierItemVm(tier, this.jewelleryClass);
      }

      return vm;
    });
  }

  buildJewelleryTierItemVm(tier: JewelleryTierType, itemClass: string): JewelleryTierItemVm[] {
    return this.jewelleryTierData
      .filter((item) => item.tier === tier && (itemClass === JewelleryType.All || item.itemClass === itemClass))
      .filter((item) => !this.changedOnly || item.originalTier !== undefined)
      .map((tieredJewellery) => {
        const vm: JewelleryTierItemVm = {
          name: tieredJewellery.name,
          originalTier: tieredJewellery.originalTier,
          isRareOnly: this.rareOnlyJewellery.has(tieredJewellery.name),
        };
        const flatItemData = this.itemDataService.getFlatItemDataByBaseTypeName(tieredJewellery.name);
        if (flatItemData) {
          vm.image = flatItemData.baseType.image;
          vm.restricted = flatItemData.baseType.restricted;
          vm.restrictedContent = flatItemData.baseType.restrictedContent;
        }
        return vm;
      });
  }

  changeJewelleryClass(): void {
    this.rebuildJewelleryTierDataVm();
  }

  rebuildJewelleryTierDataVm(): void {
    this.selectedJewelleryTierDataVm = this.buildJewelleryTierDataVm();
  }

  getTierStyle(jewelleryTier: JewelleryTierType) {
    const tierColor = jewelleryTier === 'Other' ? 'white'
      : jewelleryTier === 'Special' ? 'orange'
        : jewelleryTier === 'Good' ? 'red'
          : 'purple';
    return 'tier-' + tierColor;
  }

  onDragStart(event: DragEvent, item: JewelleryTierItemVm) {
    this.draggedItem = item;
    event.dataTransfer?.setData('item', JSON.stringify(item));
  }

  onDragEnd() {
    this.draggedItem = null;
  }

  onDragEnter(event: DragEvent, tier: JewelleryTierVm) {
    event.preventDefault();
    tier.dragCounter = (tier.dragCounter || 0) + 1;
    tier.isDragOver = true;
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    event.dataTransfer!.dropEffect = 'move';
  }

  onDragLeave(event: DragEvent, tier: JewelleryTierVm) {
    tier.dragCounter = (tier.dragCounter || 0) - 1;
    if (tier.dragCounter === 0) {
      tier.isDragOver = false;
    }
  }

  onDrop(event: DragEvent, targetTier: JewelleryTierVm) {
    event.preventDefault();
    targetTier.dragCounter = 0;
    targetTier.isDragOver = false;
    const itemJson = event.dataTransfer!.getData('item');
    if (!itemJson) return;
    const item = JSON.parse(itemJson) as JewelleryTierItemVm;
    this.filterService.updateJewelleryItemTier(item.name, targetTier.tier);
    this.jewelleryTierData = this.jewelleryTierService.getJewelleryTierData();
    this.rebuildJewelleryTierDataVm();
    this.draggedItem = null;
  }

  resetToOriginalTier(item: JewelleryTierItemVm) {
    if (!item.originalTier) return;
    this.filterService.updateJewelleryItemTier(item.name, item.originalTier);
    this.jewelleryTierData = this.jewelleryTierService.getJewelleryTierData();
    this.rebuildJewelleryTierDataVm();
  }

  private applyNavigationTarget(target: JewelleryTierNavigationTarget): void {
    this.jewelleryClass = target.itemClass as JewelleryType;
    this.rebuildJewelleryTierDataVm();
  }
}
